import React, { useState, useRef, useEffect } from 'react';
import { formatCurrency } from '../../utils/currency';

interface Account {
  id: string;
  name: string;
  type: 'cash' | 'bank' | 'other';
  balance: number;
  bank_name?: string;
  account_number?: string;
}

interface AccountSelectProps {
  accounts: Account[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
}

const AccountSelect: React.FC<AccountSelectProps> = ({
  accounts,
  value,
  onChange,
  placeholder = 'Select Account',
  label,
  required = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>(accounts);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter accounts based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAccounts(accounts);
    } else {
      const filtered = accounts.filter(account =>
        account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (account.bank_name && account.bank_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (account.account_number && account.account_number.includes(searchTerm))
      );
      setFilteredAccounts(filtered);
    }
  }, [searchTerm, accounts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get selected account
  const selectedAccount = accounts.find(account => account.id === value);

  const handleSelect = (accountId: string) => {
    onChange(accountId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus input when opening
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearchTerm('');
    }
  };

  const getAccountTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      cash: 'Cash',
      bank: 'Bank',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getAccountTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      cash: 'bg-green-100 text-green-800',
      bank: 'bg-blue-100 text-blue-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggle}
        className="relative w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      >
        <span className="block truncate">
          {selectedAccount ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-medium">{selectedAccount.name}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAccountTypeColor(selectedAccount.type)}`}>
                  {getAccountTypeLabel(selectedAccount.type)}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {formatCurrency(selectedAccount.balance)}
              </span>
            </div>
          ) : (
            placeholder
          )}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
          {/* Search input */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-3 py-2">
            <input
              ref={inputRef}
              type="text"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Account options */}
          <div className="max-h-64 overflow-y-auto">
            {filteredAccounts.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                No accounts found
              </div>
            ) : (
              filteredAccounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => handleSelect(account.id)}
                  className={`w-full px-3 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    value === account.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {account.name}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAccountTypeColor(account.type)}`}>
                          {getAccountTypeLabel(account.type)}
                        </span>
                      </div>

                      {/* Bank details for bank accounts */}
                      {account.type === 'bank' && (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          {account.bank_name && (
                            <div>Bank: {account.bank_name}</div>
                          )}
                          {account.account_number && (
                            <div>Account: {account.account_number}</div>
                          )}
                        </div>
                      )}

                      {/* Account ID */}
                      <div className="text-xs text-gray-400 mt-1">
                        ID: {account.id.slice(0, 8)}...
                      </div>
                    </div>

                    {/* Balance */}
                    <div className="text-right ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(account.balance)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Balance
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSelect;